import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Send, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import {
  RequisitionApproveModal,
  type RequisitionApproveTarget,
} from "@/features/orders/components/RequisitionApproveModal";
import { RequisitionRejectModal } from "@/features/orders/components/RequisitionRejectModal";
import { PRIORITY_LABELS } from "@/features/orders/types";
import { OfficialFeeOrderLink } from "@/features/official_fees/components/OfficialFeeOrderLink";
import { OfficialFeeRejectModal } from "@/features/official_fees/components/OfficialFeeRejectModal";
import { useApproveOfficialFee } from "@/features/official_fees/hooks/useOfficialFees";
import { FEE_APPROVAL_THRESHOLD } from "@/features/official_fees/utils/feeThreshold";
import {
  ApplicationApproveModal,
  ApplicationReturnModal,
} from "@/features/power_projects/components/wnioski/ApplicationApprovalModals";
import type { OfficialApplicationQueueRow } from "@/features/power_projects/types";
import { apiErr } from "@/features/power_projects/utils";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  useDirectorApprovals,
  useInvalidateDirectorApprovals,
} from "../hooks/useDirectorApprovals";
import { APPROVAL_KIND_LABELS, type ApprovalKind, type ApprovalRow } from "../types";

const DAY_MS = 24 * 60 * 60 * 1000;

const KIND_COLORS: Record<ApprovalKind, "blue" | "orange" | "purple"> = {
  requisition: "blue",
  official_fee: "orange",
  application: "purple",
};

const SELECT_CLS =
  "text-sm border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)] text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

/** „dziś" / „1 dzień" / „N dni" — kolor rośnie z czasem czekania. */
function WaitingFor({ since }: { since: string | null }) {
  if (!since) return <span className="text-[var(--color-text-muted)]">—</span>;
  const days = Math.floor((Date.now() - new Date(since).getTime()) / DAY_MS);
  const text = days <= 0 ? "dziś" : days === 1 ? "1 dzień" : `${days} dni`;
  const color = days >= 7 ? "red" : days >= 3 ? "yellow" : "gray";
  return (
    <Badge color={color} title={`Od ${formatDate(since)}`}>
      {text}
    </Badge>
  );
}

/**
 * „Do zatwierdzenia" — zbiorczy widok dyrektora: zapotrzebowania, opłaty urzędowe, których
 * nie może zaakceptować koordynator, i (gdy obieg działa) wnioski urzędowe, w jednej tabeli.
 * To DRUGA droga do tych samych decyzji: okna zatwierdzania/odrzucania są komponentami
 * z widoków modułów (te same pola, te same endpointy), więc warunki, historia i powiadomienia
 * są identyczne jak w oryginalnych widokach. Bez plakietki w menu: liczniki mają moduły.
 */
export function DirectorApprovalsPage() {
  const [allFees, setAllFees] = useState(false);
  const [kindFilter, setKindFilter] = useState<ApprovalKind | "">("");
  const { data, isLoading, isError, error, isFetching } = useDirectorApprovals(allFees);
  const refresh = useInvalidateDirectorApprovals();

  const approveFee = useApproveOfficialFee();

  // The requisition list's approve dialog (deadline, order type, automatic line items).
  const [approveRequisition, setApproveRequisition] = useState<RequisitionApproveTarget | null>(null);
  const [rejectRequisitionId, setRejectRequisitionId] = useState<string | null>(null);
  const [rejectFeeId, setRejectFeeId] = useState<string | null>(null);
  const [approveApplication, setApproveApplication] = useState<OfficialApplicationQueueRow | null>(null);
  const [returnApplication, setReturnApplication] = useState<OfficialApplicationQueueRow | null>(null);

  const kinds = data?.meta.kinds ?? [];
  const counts = data?.meta.counts ?? {};
  const rows = useMemo(() => {
    const all = data?.data ?? [];
    return kindFilter ? all.filter((r) => r.kind === kindFilter) : all;
  }, [data, kindFilter]);

  if (isLoading) return <PageLoader />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Do zatwierdzenia</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
          <p className="text-sm text-[var(--color-text-secondary)] mt-2 max-w-3xl">
            Wszystko, co czeka na Twoją decyzję, w jednym miejscu — najdłużej czekające na górze.
            Te same pozycje możesz nadal zatwierdzać w ich modułach.
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {kinds.includes("official_fee") && (
            <label
              className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)] cursor-pointer select-none"
              title={`Domyślnie widać tylko opłaty powyżej ${FEE_APPROVAL_THRESHOLD} zł i te z przedsięwzięć bez koordynatora — resztę może zaakceptować koordynator.`}
            >
              <input
                type="checkbox"
                checked={allFees}
                onChange={(e) => setAllFees(e.target.checked)}
                className="rounded border-[var(--color-border)] cursor-pointer accent-[var(--color-primary)]"
              />
              Także opłaty w limicie koordynatora (do {FEE_APPROVAL_THRESHOLD} zł)
            </label>
          )}
          {kinds.length > 1 && (
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as ApprovalKind | "")}
              className={SELECT_CLS}
              title="Rodzaj pozycji"
            >
              <option value="">Wszystkie rodzaje ({data?.meta.count ?? 0})</option>
              {kinds.map((k) => (
                <option key={k} value={k}>
                  {APPROVAL_KIND_LABELS[k]} ({counts[k] ?? 0})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {approveFee.isError && (
        <p className="mb-3 text-sm text-red-600">
          Nie udało się zatwierdzić opłaty: {apiErr(approveFee.error)}
        </p>
      )}

      {isError ? (
        <p className="text-sm text-red-600">{apiErr(error, "Nie udało się pobrać listy.")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          {kindFilter ? "Brak pozycji tego rodzaju do zatwierdzenia." : "Nic nie czeka na Twoją decyzję."}
        </p>
      ) : (
        <div
          className={`rounded-lg border border-[var(--color-border)] overflow-hidden bg-[var(--color-bg-surface)] ${isFetching ? "opacity-70" : ""}`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]">
                <tr>
                  <th className="text-left px-3 py-3 font-medium">Rodzaj</th>
                  <th className="text-left px-3 py-3 font-medium">Pozycja</th>
                  <th className="text-left px-3 py-3 font-medium">Przedsięwzięcie</th>
                  <th className="text-right px-3 py-3 font-medium">Kwota</th>
                  <th className="text-left px-3 py-3 font-medium">Zgłaszający</th>
                  <th className="text-left px-3 py-3 font-medium">Czeka</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {rows.map((row) => (
                  <tr key={`${row.kind}:${row.id}`} className="align-top hover:bg-[var(--color-bg-elevated)]">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <Badge color={KIND_COLORS[row.kind]}>{APPROVAL_KIND_LABELS[row.kind]}</Badge>
                    </td>
                    <td className="px-3 py-3">
                      <div className="min-w-[200px] max-w-[300px]">
                        <Link
                          to={row.detail_path}
                          className="font-medium text-[var(--color-text-primary)] hover:underline line-clamp-2"
                          title={row.title}
                        >
                          {row.title || "—"}
                        </Link>
                        {row.subtitle && (
                          <div className="text-xs text-[var(--color-text-secondary)] truncate" title={row.subtitle}>
                            {row.subtitle}
                          </div>
                        )}
                        <RowNote row={row} />
                      </div>
                    </td>
                    <td className="px-3 py-3 text-[var(--color-text-secondary)] max-w-[170px]">
                      {row.project_name || "—"}
                      {row.kind === "official_fee" && row.extra.order_id && row.extra.order_number && (
                        <div className="text-xs mt-0.5 break-words">
                          <span className="text-[var(--color-text-muted)]">Zamówienie </span>
                          <OfficialFeeOrderLink
                            orderId={row.extra.order_id}
                            orderNumber={row.extra.order_number}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right font-mono whitespace-nowrap text-[var(--color-text-primary)]">
                      {row.amount === null ? (
                        <span className="text-[var(--color-text-muted)]">—</span>
                      ) : (
                        <span
                          title={
                            row.kind === "requisition" && row.extra.amount_partial
                              ? "Suma tylko z pozycji, do których masz uprawnienia finansowe"
                              : undefined
                          }
                        >
                          {formatAmount(row.amount)} zł
                          {row.kind === "requisition" && row.extra.amount_partial && "*"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[var(--color-text-secondary)] max-w-[130px]">{row.requested_by_name || "—"}</td>
                    <td className="px-3 py-3 whitespace-nowrap"><WaitingFor since={row.waiting_since} /></td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <RowActions
                          row={row}
                          feeApproving={approveFee.isPending && approveFee.variables === row.id}
                          onApproveFee={() => approveFee.mutate(row.id, { onSuccess: refresh })}
                          onRejectFee={() => setRejectFeeId(row.id)}
                          onApproveRequisition={() =>
                            row.kind === "requisition" &&
                            setApproveRequisition({
                              id: row.id,
                              deadline: row.extra.deadline,
                              suggestedOrderType: row.extra.suggested_order_type,
                            })
                          }
                          onRejectRequisition={() => setRejectRequisitionId(row.id)}
                          onApproveApplication={setApproveApplication}
                          onReturnApplication={setReturnApplication}
                        />
                        <Link
                          to={row.detail_path}
                          title="Otwórz w module"
                          className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                        >
                          <ExternalLink size={14} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RequisitionApproveModal
        target={approveRequisition}
        onClose={() => setApproveRequisition(null)}
        onApproved={refresh}
      />
      <RequisitionRejectModal
        requisitionId={rejectRequisitionId ?? ""}
        open={!!rejectRequisitionId}
        onClose={() => setRejectRequisitionId(null)}
        onRejected={refresh}
      />
      <OfficialFeeRejectModal
        feeId={rejectFeeId}
        open={!!rejectFeeId}
        onClose={() => setRejectFeeId(null)}
        onRejected={refresh}
      />
      {approveApplication && (
        <ApplicationApproveModal
          row={approveApplication}
          onClose={() => setApproveApplication(null)}
          onDone={refresh}
        />
      )}
      {returnApplication && (
        <ApplicationReturnModal
          row={returnApplication}
          onClose={() => setReturnApplication(null)}
          onDone={refresh}
        />
      )}
    </div>
  );
}

/** Trzecia linijka komórki „Pozycja": to, co decyduje o pilności albo o tym, czemu czeka na dyrektora. */
function RowNote({ row }: { row: ApprovalRow }) {
  const cls = "text-xs text-[var(--color-text-muted)] mt-0.5";
  if (row.kind === "requisition") {
    return (
      <div className={cls}>
        Priorytet: {PRIORITY_LABELS[row.extra.priority]}
        {row.extra.deadline && <> · termin {formatDate(row.extra.deadline)}</>}
      </div>
    );
  }
  if (!row.note) return null;
  const warn = row.kind === "official_fee" && row.extra.above_threshold;
  return <div className={warn ? "text-xs mt-0.5 text-amber-700 dark:text-amber-400" : cls}>{row.note}</div>;
}

function RowActions({
  row,
  feeApproving,
  onApproveFee,
  onRejectFee,
  onApproveRequisition,
  onRejectRequisition,
  onApproveApplication,
  onReturnApplication,
}: {
  row: ApprovalRow;
  feeApproving: boolean;
  onApproveFee: () => void;
  onRejectFee: () => void;
  onApproveRequisition: () => void;
  onRejectRequisition: () => void;
  onApproveApplication: (r: OfficialApplicationQueueRow) => void;
  onReturnApplication: (r: OfficialApplicationQueueRow) => void;
}) {
  // Labels and order follow each module's own buttons.
  switch (row.kind) {
    case "requisition":
      return (
        <>
          <Button size="sm" onClick={onApproveRequisition}>
            Zatwierdź
          </Button>
          <Button size="sm" variant="danger" onClick={onRejectRequisition}>
            Odrzuć
          </Button>
        </>
      );
    case "official_fee":
      return (
        <>
          <Button size="sm" loading={feeApproving} onClick={onApproveFee}>
            Akceptuj
          </Button>
          <Button size="sm" variant="danger" onClick={onRejectFee}>
            Odrzuć
          </Button>
        </>
      );
    case "application":
      return (
        <>
          <Button size="sm" onClick={() => onApproveApplication(row.extra.queue_row)}
                  title="Zatwierdź i przekaż do wysłania">
            <Send size={12} className="mr-1" /> Zatwierdź
          </Button>
          <Button
            size="sm"
            variant="secondary"
            title="Zwróć do poprawy"
            onClick={() => onReturnApplication(row.extra.queue_row)}
          >
            <Undo2 size={12} className="mr-1" /> Zwróć
          </Button>
        </>
      );
  }
}
